import React, { useMemo, useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight } from './Icons';
import BottomNavigation from './BottomNavigation';
import GrammarAnalysisDisplay from './GrammarAnalysisDisplay';
import AIDrillButton from './AIDrillButton';
import { grammarTopicsData, getTopicById, getNextTopic, getPreviousTopic } from '../data/grammarTopicsData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const GrammarTopicLearningPage = ({ 
  onBack, 
  userPreferences, 
  groupId, 
  topicId, 
  mode = 'read',
  onModeChange
}) => {
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [testAnswers, setTestAnswers] = useState({});
  const [testResults, setTestResults] = useState(null);
  const [isTestComplete, setIsTestComplete] = useState(false);
  
  const { displayLanguage } = userPreferences;

  // Load grammar groups/topics/examples from Supabase if available, fallback to static
  const { data: remote } = useSupabaseData({
    table: 'eng_grammargroups',
    mapRow: (row) => row,
    getFallback: () => null
  });

  const { data, topic: topicFromRemote, groupsSequence } = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) {
      return { data: grammarTopicsData, topic: getTopicById(groupId, topicId), groupsSequence: null };
    }
    const rows = Array.isArray(remote) ? remote : [remote];
    const groupsMap = new Map();
    rows.forEach((r) => {
      const gId = r.group_id || r.group || r.id;
      if (!groupsMap.has(gId)) {
        groupsMap.set(gId, {
          id: gId,
          icon: r.group_icon || '📚',
          title: {
            tc: r.group_title_tc || r.group_title || '語法主題',
            sc: r.group_title_sc || r.group_title || '语法主题',
            en: r.group_title_en || r.group_title || 'Grammar Topics'
          },
          subtitle: {
            tc: r.group_subtitle_tc || '',
            sc: r.group_subtitle_sc || '',
            en: r.group_subtitle_en || ''
          },
          difficulty: r.group_difficulty || 'beginner',
          topics: []
        });
      }
      // Topic row
      if (r.topic_id || r.topic_title_en || r.topic_title_tc || r.topic_title_sc) {
        const tId = r.topic_id || r.id;
        let topic = groupsMap.get(gId).topics.find(t => t.id === tId);
        if (!topic) {
          topic = {
            id: tId,
            icon: r.topic_icon || '🔹',
            title: {
              tc: r.topic_title_tc || r.topic_title || '',
              sc: r.topic_title_sc || r.topic_title || '',
              en: r.topic_title_en || r.topic_title || ''
            },
            description: {
              tc: r.topic_desc_tc || r.topic_description || '',
              sc: r.topic_desc_sc || r.topic_description || '',
              en: r.topic_desc_en || r.topic_description || ''
            },
            teachingNotes: {
              tc: r.teaching_notes_tc || '',
              sc: r.teaching_notes_sc || '',
              en: r.teaching_notes_en || ''
            },
            difficulty: r.topic_difficulty || groupsMap.get(gId).difficulty || 'beginner',
            examples: []
          };
          groupsMap.get(gId).topics.push(topic);
        }
        // Example attached in same row
        if (r.example_id || r.example_sentence || r.example_analysis || r.example_translation_en) {
          topic.examples.push({
            id: r.example_id || `${tId}_${topic.examples.length + 1}`,
            sentence: r.example_sentence || '',
            analysis: r.example_analysis || '',
            translation: {
              tc: r.example_translation_tc || '',
              sc: r.example_translation_sc || '',
              en: r.example_translation_en || ''
            }
          });
        }
      }
    });
    const groups = Array.from(groupsMap.values());
    const topicFound = groups.find(g => g.id === groupId)?.topics.find(t => t.id === topicId) || null;
    const sequence = groups.map(g => ({ id: g.id, topics: g.topics.map(t => t.id) }));
    return {
      data: {
        overview: grammarTopicsData.overview,
        learningModes: grammarTopicsData.learningModes,
        grammarGroups: groups.length > 0 ? groups : grammarTopicsData.grammarGroups,
        userProgress: grammarTopicsData.userProgress,
        navigation: grammarTopicsData.navigation,
        statusMessages: grammarTopicsData.statusMessages
      },
      topic: topicFound,
      groupsSequence: sequence
    };
  }, [remote, groupId, topicId]);

  const getText = (textObj) => {
    return textObj[displayLanguage] || textObj.en;
  };

  const topic = topicFromRemote || getTopicById(groupId, topicId);
  const currentExample = topic?.examples[currentExampleIndex];

  useEffect(() => {
    setCurrentExampleIndex(0);
    setTestAnswers({});
    setTestResults(null);
    setIsTestComplete(false);
  }, [groupId, topicId, mode]);

  const handleNextExample = () => {
    if (currentExampleIndex < topic.examples.length - 1) {
      setCurrentExampleIndex(currentExampleIndex + 1);
    }
  };

  const handlePreviousExample = () => {
    if (currentExampleIndex > 0) {
      setCurrentExampleIndex(currentExampleIndex - 1);
    }
  };

  const handleTestAnswer = (exampleId, answer) => {
    setTestAnswers(prev => ({
      ...prev,
      [exampleId]: answer
    }));
  };

  const handleSubmitTest = () => {
    const results = topic.examples.map(example => {
      const userAnswer = testAnswers[example.id] || '';
      const isCorrect = userAnswer.toLowerCase().includes(example.analysis.toLowerCase());
      return {
        exampleId: example.id,
        userAnswer,
        correctAnswer: example.analysis,
        isCorrect
      };
    });
    
    setTestResults(results);
    setIsTestComplete(true);
    
    // Calculate score
    const correctCount = results.filter(r => r.isCorrect).length;
    const score = Math.round((correctCount / results.length) * 100);
    
    // Update progress (would be replaced with actual Supabase call)
    console.log(`Test completed with score: ${score}%`);
  };

  const handleNextTopic = () => {
    const nextTopic = groupsSequence
      ? (() => {
          const gIdx = groupsSequence.findIndex(g => g.id === groupId);
          if (gIdx === -1) return null;
          const tIdx = groupsSequence[gIdx].topics.findIndex(t => t === topicId);
          if (tIdx !== -1 && tIdx < groupsSequence[gIdx].topics.length - 1) {
            return { id: groupsSequence[gIdx].topics[tIdx + 1] };
          }
          if (gIdx < groupsSequence.length - 1) {
            const nextGroup = groupsSequence[gIdx + 1];
            return { id: nextGroup.topics[0] };
          }
          return null;
        })()
      : getNextTopic(groupId, topicId);
    if (nextTopic) {
      // Navigate to next topic
      console.log('Navigate to next topic:', nextTopic.id);
    }
  };

  const handlePreviousTopic = () => {
    const prevTopic = groupsSequence
      ? (() => {
          const gIdx = groupsSequence.findIndex(g => g.id === groupId);
          if (gIdx === -1) return null;
          const tIdx = groupsSequence[gIdx].topics.findIndex(t => t === topicId);
          if (tIdx > 0) {
            return { id: groupsSequence[gIdx].topics[tIdx - 1] };
          }
          if (gIdx > 0) {
            const prevGroup = groupsSequence[gIdx - 1];
            return { id: prevGroup.topics[prevGroup.topics.length - 1] };
          }
          return null;
        })()
      : getPreviousTopic(groupId, topicId);
    if (prevTopic) {
      // Navigate to previous topic
      console.log('Navigate to previous topic:', prevTopic.id);
    }
  };

  if (!topic) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">
            {getText({ tc: '主題未找到', sc: '主题未找到', en: 'Topic Not Found' })}
          </h2>
          <button 
            onClick={onBack}
            className="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            {getText({ tc: '返回', sc: '返回', en: 'Back' })}
          </button>
        </div>
      </div>
    );
  }

  const renderReadMode = () => (
    <div className="px-6">
      {/* Topic Header */}
      <div className="bg-gray-800/50 rounded-lg p-4 mb-6 border border-gray-600">
        <div className="flex items-center space-x-3 mb-3">
          <span className="text-2xl">{topic.icon}</span>
          <div>
            <h2 className="text-lg font-bold">{getText(topic.title)}</h2>
            <p className="text-gray-300">{getText(topic.description)}</p>
          </div>
        </div>
        <p className="text-sm text-gray-300">{getText(topic.teachingNotes)}</p>
      </div>

      {/* Example Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={handlePreviousExample}
          disabled={currentExampleIndex === 0}
          className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowLeft size={16} />
        </button>
        <span className="text-sm text-gray-300">
          {currentExampleIndex + 1} / {topic.examples.length}
        </span>
        <button
          onClick={handleNextExample}
          disabled={currentExampleIndex === topic.examples.length - 1}
          className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowRight size={16} />
        </button>
      </div>

      {/* Current Example */}
      <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600 mb-6">
        <h3 className="text-lg font-semibold mb-4">
          {getText({ tc: '例句', sc: '例句', en: 'Example' })} {currentExampleIndex + 1}
        </h3>
        
        <GrammarAnalysisDisplay
          sentence={currentExample.sentence}
          analysis={currentExample.analysis}
          translation={currentExample.translation}
          displayLanguage={displayLanguage}
        />
      </div>
    </div>
  );

  const renderStudyMode = () => (
    <div className="px-6">
      {/* Topic Header */}
      <div className="bg-gray-800/50 rounded-lg p-4 mb-6 border border-gray-600">
        <div className="flex items-center space-x-3 mb-3">
          <span className="text-2xl">{topic.icon}</span>
          <div>
            <h2 className="text-lg font-bold">{getText(topic.title)}</h2>
            <p className="text-gray-300">{getText(topic.description)}</p>
          </div>
        </div>
      </div>

      {/* Interactive Study */}
      <div className="space-y-6">
        {topic.examples.map((example, index) => (
          <div key={example.id} className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
            <h3 className="text-lg font-semibold mb-4">
              {getText({ tc: '練習', sc: '练习', en: 'Exercise' })} {index + 1}
            </h3>
            
            <div className="space-y-4">
              <div className="p-4 bg-gray-700/50 rounded-lg">
                <h4 className="text-sm text-gray-400 mb-2">
                  {getText({ tc: '分析這個句子', sc: '分析这个句子', en: 'Analyze this sentence' })}
                </h4>
                <p className="text-lg mb-3">{example.sentence}</p>
                
                                                  <div className="grid grid-cols-2 gap-3 text-sm">
                   <div className="p-3 bg-blue-900/30 rounded border border-blue-600 min-h-[80px] flex flex-col">
                     <span className="text-blue-300 font-semibold block mb-1">S (Subject):</span>
                     <p className="text-blue-200 text-xs break-words leading-relaxed flex-1 overflow-hidden">
                       {(() => {
                         // Parse the entire analysis string into components
                         const components = {};
                         const regex = /([^(]+?)\s*\(([SVOCM][^)]*)\)/g;
                         let match;
                         
                         while ((match = regex.exec(example.analysis)) !== null) {
                           const content = match[1].trim();
                           const tag = match[2];
                           
                           if (tag.startsWith('S')) {
                             components.subject = content;
                           } else if (tag.startsWith('V')) {
                             components.verb = content;
                           } else if (tag.startsWith('O')) {
                             components.object = content;
                           } else if (tag.startsWith('C') || tag.startsWith('M')) {
                             components.modifier = content;
                           }
                         }
                         
                         return components.subject || 'N/A';
                       })()}
                     </p>
                   </div>
                   <div className="p-3 bg-green-900/30 rounded border border-green-600 min-h-[80px] flex flex-col">
                     <span className="text-green-300 font-semibold block mb-1">V (Verb):</span>
                     <p className="text-green-200 text-xs break-words leading-relaxed flex-1 overflow-hidden">
                       {(() => {
                         // Parse the entire analysis string into components
                         const components = {};
                         const regex = /([^(]+?)\s*\(([SVOCM][^)]*)\)/g;
                         let match;
                         
                         while ((match = regex.exec(example.analysis)) !== null) {
                           const content = match[1].trim();
                           const tag = match[2];
                           
                           if (tag.startsWith('S')) {
                             components.subject = content;
                           } else if (tag.startsWith('V')) {
                             components.verb = content;
                           } else if (tag.startsWith('O')) {
                             components.object = content;
                           } else if (tag.startsWith('C') || tag.startsWith('M')) {
                             components.modifier = content;
                           }
                         }
                         
                         return components.verb || 'N/A';
                       })()}
                     </p>
                   </div>
                   <div className="p-3 bg-yellow-900/30 rounded border border-yellow-600 min-h-[80px] flex flex-col">
                     <span className="text-yellow-300 font-semibold block mb-1">O (Object):</span>
                     <p className="text-yellow-200 text-xs break-words leading-relaxed flex-1 overflow-hidden">
                       {(() => {
                         // Parse the entire analysis string into components
                         const components = {};
                         const regex = /([^(]+?)\s*\(([SVOCM][^)]*)\)/g;
                         let match;
                         
                         while ((match = regex.exec(example.analysis)) !== null) {
                           const content = match[1].trim();
                           const tag = match[2];
                           
                           if (tag.startsWith('S')) {
                             components.subject = content;
                           } else if (tag.startsWith('V')) {
                             components.verb = content;
                           } else if (tag.startsWith('O')) {
                             components.object = content;
                           } else if (tag.startsWith('C') || tag.startsWith('M')) {
                             components.modifier = content;
                           }
                         }
                         
                         return components.object || 'N/A';
                       })()}
                     </p>
                   </div>
                   <div className="p-3 bg-purple-900/30 rounded border border-purple-600 min-h-[80px] flex flex-col">
                     <span className="text-purple-300 font-semibold block mb-1">C/M (Complement/Modifier):</span>
                     <p className="text-purple-200 text-xs break-words leading-relaxed flex-1 overflow-hidden">
                       {(() => {
                         // Parse the entire analysis string into components
                         const components = {};
                         const regex = /([^(]+?)\s*\(([SVOCM][^)]*)\)/g;
                         let match;
                         
                         while ((match = regex.exec(example.analysis)) !== null) {
                           const content = match[1].trim();
                           const tag = match[2];
                           
                           if (tag.startsWith('S')) {
                             components.subject = content;
                           } else if (tag.startsWith('V')) {
                             components.verb = content;
                           } else if (tag.startsWith('O')) {
                             components.object = content;
                           } else if (tag.startsWith('C') || tag.startsWith('M')) {
                             components.modifier = content;
                           }
                         }
                         
                         return components.modifier || 'N/A';
                       })()}
                     </p>
                   </div>
                 </div>
              </div>
              
              {/* AI Drill Button */}
              <AIDrillButton
                example={example}
                topicData={topic}
                userPreferences={userPreferences}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTestMode = () => (
    <div className="px-6">
      {/* Topic Header */}
      <div className="bg-gray-800/50 rounded-lg p-4 mb-6 border border-gray-600">
        <div className="flex items-center space-x-3 mb-3">
          <span className="text-2xl">{topic.icon}</span>
          <div>
            <h2 className="text-lg font-bold">{getText(topic.title)}</h2>
            <p className="text-gray-300">{getText(topic.description)}</p>
          </div>
        </div>
      </div>

      {!isTestComplete ? (
        /* Test Questions */
        <div className="space-y-6">
          <div className="bg-yellow-900/30 rounded-lg p-4 border border-yellow-600 mb-6">
            <h3 className="text-yellow-200 font-semibold mb-2">
              {getText({ tc: '測試說明', sc: '测试说明', en: 'Test Instructions' })}
            </h3>
            <p className="text-yellow-100 text-sm">
              {getText({ 
                tc: '請分析每個句子的語法結構，標註 S (主語)、V (動詞)、O (受詞)、C (補語)、M (修飾語)',
                sc: '请分析每个句子的语法结构，标注 S (主语)、V (动词)、O (宾语)、C (补语)、M (修饰语)',
                en: 'Please analyze the grammatical structure of each sentence, marking S (Subject), V (Verb), O (Object), C (Complement), M (Modifier)'
              })}
            </p>
          </div>

          {topic.examples.map((example, index) => (
            <div key={example.id} className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
              <h3 className="text-lg font-semibold mb-4">
                {getText({ tc: '問題', sc: '问题', en: 'Question' })} {index + 1}
              </h3>
              
              <div className="space-y-4">
                <div className="p-4 bg-gray-700/50 rounded-lg">
                  <h4 className="text-sm text-gray-400 mb-2">
                    {getText({ tc: '分析這個句子', sc: '分析这个句子', en: 'Analyze this sentence' })}
                  </h4>
                  <p className="text-lg mb-3">{example.sentence}</p>
                  
                  <textarea
                    value={testAnswers[example.id] || ''}
                    onChange={(e) => handleTestAnswer(example.id, e.target.value)}
                    placeholder={getText({ 
                      tc: '請輸入語法分析，例如：I (S) finished (V) my homework (O)',
                      sc: '请输入语法分析，例如：I (S) finished (V) my homework (O)',
                      en: 'Please enter grammar analysis, e.g.: I (S) finished (V) my homework (O)'
                    })}
                    className="w-full p-3 bg-gray-600 rounded-lg text-white border border-gray-500 focus:border-blue-400 focus:outline-none"
                    rows="3"
                  />
                </div>
              </div>
            </div>
          ))}

          <div className="flex justify-center">
            <button
              onClick={handleSubmitTest}
              className="px-6 py-3 bg-green-600 rounded-lg hover:bg-green-700 font-semibold"
            >
              {getText({ tc: '提交測試', sc: '提交测试', en: 'Submit Test' })}
            </button>
          </div>
        </div>
      ) : (
        /* Test Results */
        <div className="space-y-6">
          <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
            <h3 className="text-lg font-semibold mb-4">
              {getText({ tc: '測試結果', sc: '测试结果', en: 'Test Results' })}
            </h3>
            
            <div className="grid grid-cols-3 gap-4 text-center mb-6">
              <div>
                <div className="text-2xl font-bold text-green-400">
                  {testResults.filter(r => r.isCorrect).length}
                </div>
                <div className="text-sm text-gray-300">
                  {getText({ tc: '正確', sc: '正确', en: 'Correct' })}
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-400">
                  {testResults.filter(r => !r.isCorrect).length}
                </div>
                <div className="text-sm text-gray-300">
                  {getText({ tc: '錯誤', sc: '错误', en: 'Incorrect' })}
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-400">
                  {Math.round((testResults.filter(r => r.isCorrect).length / testResults.length) * 100)}%
                </div>
                <div className="text-sm text-gray-300">
                  {getText({ tc: '得分', sc: '得分', en: 'Score' })}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {testResults.map((result, index) => (
                <div key={result.exampleId} className="p-4 rounded-lg border">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`text-lg ${result.isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                      {result.isCorrect ? '✅' : '❌'}
                    </span>
                    <span className="font-semibold">
                      {getText({ tc: '問題', sc: '问题', en: 'Question' })} {index + 1}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-400">
                        {getText({ tc: '你的答案', sc: '你的答案', en: 'Your Answer' })}:
                      </span>
                      <p className="text-gray-200">{result.userAnswer || getText({ tc: '未作答', sc: '未作答', en: 'No answer' })}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">
                        {getText({ tc: '正確答案', sc: '正确答案', en: 'Correct Answer' })}:
                      </span>
                      <p className="text-green-200">{result.correctAnswer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (mode) {
      case 'read':
        return renderReadMode();
      case 'study':
        return renderStudyMode();
      case 'test':
        return renderTestMode();
      default:
        return renderReadMode();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="px-6 pt-12 pb-4">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-white">
            <ArrowLeft size={24} />
          </button>
          <div className="text-center">
            <h1 className="text-xl font-bold">
              {getText(topic.title)}
            </h1>
            <p className="text-sm text-gray-300">
              {getText(data.learningModes[mode].title)}
            </p>
          </div>
          <div className="w-6"></div> {/* Spacer for centering */}
        </div>
      </div>

      {/* Learning Modes Selection */}
      <div className="px-6 mb-4">
        <h2 className="text-lg font-bold mb-3">
          {getText({ tc: '學習模式', sc: '学习模式', en: 'Learning Modes' })}
        </h2>
        <div className="grid grid-cols-1 gap-2">
          {/* Read Mode */}
          <div 
            className={`p-3 rounded-lg border cursor-pointer transition-all ${
              mode === 'read' 
                ? 'bg-blue-600/30 border-blue-500' 
                : 'bg-gray-800/50 border-gray-600 hover:bg-gray-700/50'
            }`}
            onClick={() => onModeChange && onModeChange('read')}
          >
            <div className="flex items-center space-x-3">
              <span className="text-xl">📖</span>
              <div className="flex-1">
                <h3 className="font-semibold text-sm">
                  {getText({ tc: '閱讀模式', sc: '阅读模式', en: 'Read Mode' })}
                </h3>
                <p className="text-xs text-gray-300">
                  {getText({ 
                    tc: '閱讀語法結構和例句以理解基本概念', 
                    sc: '阅读语法结构和例句以理解基本概念', 
                    en: 'Read grammar structures and examples to understand basic concepts' 
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Study Mode */}
          <div 
            className={`p-3 rounded-lg border cursor-pointer transition-all ${
              mode === 'study' 
                ? 'bg-green-600/30 border-green-500' 
                : 'bg-gray-800/50 border-gray-600 hover:bg-gray-700/50'
            }`}
            onClick={() => onModeChange && onModeChange('study')}
          >
            <div className="flex items-center space-x-3">
              <span className="text-xl">🎓</span>
              <div className="flex-1">
                <h3 className="font-semibold text-sm">
                  {getText({ tc: '學習模式', sc: '学习模式', en: 'Study Mode' })}
                </h3>
                <p className="text-xs text-gray-300">
                  {getText({ 
                    tc: '深入學習語法規則和應用方法', 
                    sc: '深入学习语法规则和应用方法', 
                    en: 'Deep dive into grammar rules and application methods' 
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Test Mode */}
          <div 
            className={`p-3 rounded-lg border cursor-pointer transition-all ${
              mode === 'test' 
                ? 'bg-yellow-600/30 border-yellow-500' 
                : 'bg-gray-800/50 border-gray-600 hover:bg-gray-700/50'
            }`}
            onClick={() => onModeChange && onModeChange('test')}
          >
            <div className="flex items-center space-x-3">
              <span className="text-xl">📝</span>
              <div className="flex-1">
                <h3 className="font-semibold text-sm">
                  {getText({ tc: '測試模式', sc: '测试模式', en: 'Test Mode' })}
                </h3>
                <p className="text-xs text-gray-300">
                  {getText({ 
                    tc: '通過練習測試鞏固語法知識', 
                    sc: '通过练习测试巩固语法知识', 
                    en: 'Consolidate grammar knowledge through practice tests' 
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {renderContent()}
      </div>

      {/* Navigation Buttons */}
      <div className="px-6 py-4">
        <div className="flex justify-between">
          <button
            onClick={handlePreviousTopic}
            className="px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600"
          >
            {getText({ tc: '上一個主題', sc: '上一个主题', en: 'Previous Topic' })}
          </button>
          <button
            onClick={handleNextTopic}
            className="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            {getText({ tc: '下一個主題', sc: '下一个主题', en: 'Next Topic' })}
          </button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation
        currentView="grammar_topic_learning"
        onHomeClick={onBack}
        onLearningClick={onBack}
        onChallengeClick={onBack}
        onSocialClick={onBack}
      />
    </div>
  );
};

export default GrammarTopicLearningPage;
